/** Strings of the accounting inbox (`/accounting/inbox`): `accInboxEn` is the source, `accInboxCs` must have the same keys. */
export const accInboxEn = {
  accInbox: {
    title: "Incoming documents",
    subtitle:
      "What the model read is a proposal. The document only exists once you confirm it here.",
    queueEmpty:
      "The queue is empty. Documents arrive here from the <scanner>Netlion Scanner</scanner> app on a phone, or by being forwarded to the mailbox above.",
    inQueue: "{{total}} in the queue",
    dropbox: {
      uploading: "Uploading…",
      upload: "Upload a document",
      hint: "Or drop a PDF or a photo anywhere on this box. It joins the same queue as the phone and the mailbox — read by the model, confirmed by you.",
      refused: "{{name}} was refused",
      failed: "The upload failed.",
    },
    mailbox: {
      title: "Forward an invoice by e-mail",
      description:
        "Send it to <address>{{address}}</address> and Daniela reads the attachment into this queue. She files nothing — it is still you who confirms it.",
      watching: "watching",
      notSetUp: "not set up",
      queueing: "Queueing…",
      checkNow: "Check now",
      queueFailed: "Couldn't queue the read.",
      noPassword:
        "The mailbox has no password yet (<code>DOCUMENTS_IMAP_PASS</code>), so nothing is being read.",
      lastRead: "Last read",
      isRunning: "is running",
      lastReadCounts: ": {{messages}} message(s), {{accepted}} document(s) taken.",
      notTaken: "Not taken from the last read:",
      noSender: "(no sender)",
      restricted:
        "Mail is taken from people with a Hub account and from addresses in the accounting contacts; anything else is turned away and listed here.",
    },
    fakturoid: {
      title: "Fakturoid's document box",
      description:
        "Files dropped into „Krabice na doklady“ come over every 30 minutes and stay in Fakturoid.",
      queueing: "Queueing…",
      pullNow: "Pull now",
      queueFailed: "Couldn't queue the pull.",
      noPull: "No pull has run yet.",
      lastPull: "Last pull",
      isRunning: "is running",
      counts: ": {{files}} file(s) in the box, {{added}} new here.",
    },
    scan: {
      untitled: "Scanned document",
      byEmail: "by e-mail from {{sender}}",
      senderUnverified: "Unverified sender",
      senderUnverifiedHint:
        "The mail server could not confirm the mail really came from this address ({{reason}}). Check the document with the supplier before paying it.",
      withSubject: ' — „{{subject}}"',
      fromFakturoid: "from Fakturoid's document box",
      withNote: " ({{note}})",
      unknown: "unknown",
      reading: "reading…",
      unreadable: "unreadable",
      readAgain: "Read again",
      document: "The document",
      openPdf: "Open the PDF",
      unreadableFields: "On the document but unreadable:",
      documentType: "Document type",
      kind: {
        invoice: "invoice",
        receipt: "receipt",
        credit_note: "credit note",
        other: "other",
      },
      correctsNo: "Corrects invoice no.",
      creditNoteHint:
        "Amounts are what comes back; Hub keeps them negative. A note for exactly the tax on its invoice is a refund of tax and stays off the VAT return; any other follows the invoice.",
      registrationNo: "Registration no.",
      vatNo: "VAT no.",
      documentNumber: "Document number",
      variableSymbol: "Variable symbol",
      issued: "Issued",
      taxableSupply: "Taxable supply",
      due: "Due",
      received: "Received",
      currency: "Currency",
      rateToCzk: "Rate to CZK",
      rateLoading: "Looking up the ČNB fixing…",
      rateMissing: "The ČNB has no fixing for that day — fill the rate in from the bank statement.",
      rateFound: "ČNB fixing for the taxable date: {{rate}} CZK.",
      seedDescription: "Supply per document",
      base: "Base",
      vat: "VAT",
      total: "Total",
      paidInCash: "Paid in cash on the spot",
      paidFromBank: "Paid from the bank",
      prepaid:
        "Paid from a prepaid credit: the card only topped the credit up, so no movement pays this document. Create it unpaid.",
      noMovement:
        "No bank movement fits yet (amount, date around the invoice). Create it unpaid; it pairs once the payment shows up.",
      card: "card",
      noneOfThese: "none of these, create it unpaid",
      replaces:
        "Read again from the original of <originalDocument>{{number}}</originalDocument> ({{partner}}, {{total}} {{currency}} as Fakturoid had it). Creating this document cancels that one and takes over its payments.",
      mayBeHere: "This may already be here:",
      sameNumber: "same number",
      sameAmountAndDay: "same amount and day",
      createAnyway: "It is a different document, create it anyway",
      creating: "Creating…",
      create: "Create document",
      discardReason: "Reason for discarding",
      discard: "Discard",
      duplicateConflict:
        "It looks like a document that is already here. Check it, or confirm below.",
      createFailed: "Couldn't create the document.",
      discardFailed: "Couldn't discard it.",
      rereadFailed: "Couldn't start the read.",
    },
    paymentReason: {
      variable_symbol: "variable symbol",
      amount: "amount",
      card_amount: "card amount",
      supplier_name: "supplier",
      date: "same day",
      together: "together make the amount",
    },
    fields: {
      supplier_name: "supplier",
      supplier_registration_no: "registration no.",
      supplier_vat_no: "VAT no.",
      document_number: "document number",
      variable_symbol: "variable symbol",
      issued_on: "issue date",
      taxable_on: "taxable supply date",
      due_on: "due date",
      currency: "currency",
      subtotal: "base",
      vat_total: "VAT",
      vat_rate: "VAT rate",
      total: "total",
      payment_method: "payment method",
      document_kind: "document type",
    },
    missingFile: {
      title: "The archived file is missing.",
      email:
        "The e-mail is still in the mailbox, so it can be pulled back — the row keeps its place in the queue and whatever the model already read.",
      fetching: "Fetching…",
      fetch: "Fetch again from the mailbox",
      fakturoid:
        "It came from Fakturoid's document box. Download it there, discard this row and upload the file again.",
      upload:
        "It came from an upload, so there is nothing to fetch it back from — discard the row and send the document again.",
      fetchFailed: "Couldn't fetch it again.",
    },
    supplyType: {
      service: "a service",
      goods: "goods",
      goods_with_installation: "goods with installation",
      construction: "construction work",
      import: "imported goods",
      other: "something else",
    },
    vat: {
      thisIs: "This is",
      domesticReverseCharge: 'The document says „daň odvede zákazník" (§92a)',
      supplyCode: "Code of supply (B.1)",
      supplyCodeChoose: "Choose…",
      notDeductible: "Don't deduct the VAT (not made out to the company): it stays in the cost",
      weAssess: "We assess the tax",
      supplierAccounts: "The supplier accounts for the tax",
      needsLook: " · needs a look",
      returnLabel: "Return",
      returnLines: "lines {{lines}}",
      returnDeductionOnly: "— deduction only",
      returnDeduction: ", deduction {{lines}}",
      controlStatement: " · control statement {{section}}",
      overriddenSelf: "You set this to self-assessed by hand.",
      overriddenSupplier: "You set this to the supplier's tax by hand.",
      useRules: "Use the rules again",
      disagree: "Disagree?",
      switchToSupplier: "The supplier accounts for it",
      switchToUs: "We assess it",
    },
    lines: {
      breakIntoLines: "Break it into lines",
      description: "Description",
      qty: "Qty",
      unit: "Unit",
      unitPrice: "Unit price",
      vatPct: "VAT %",
      removeLine: "remove line",
      addLine: "Add a line",
      base: "Base",
      vatWeAssess: "VAT we assess",
      vat: "VAT",
      payable: "Payable to the supplier",
      total: "Total",
    },
    supplier: {
      label: "Supplier",
      notInBook: "— not in the address book —",
      pick: "Pick from the address book…",
      nameOnDocument: "Name on the document",
      notInBookHint: "Not in the address book: it is added when the document is created.",
      adding: "Adding…",
      addNow: "Add to the address book now",
      addFailed: "Couldn't add the supplier.",
    },
    rereadAll: {
      queueFailed: "Couldn't queue the reading.",
      confirm: "Read all {{n}} waiting documents again? Edits in their forms are lost.",
      queueing: "Queueing…",
      reading: "Reading… {{left}} left",
      button: "Read all again",
      reloads: "The list reloads when the last one is read.",
      hint: "Reads the waiting documents again (supplier country, VAT payer, customer).",
    },
    duplicates: {
      summary: "Set aside as duplicates ({{n}})",
      document: "document",
      bringBack: "bring back",
      restoreFailed: "Couldn't bring it back.",
    },
  },
};

export const accInboxCs: typeof accInboxEn = {
  accInbox: {
    title: "Došlé doklady",
    subtitle: "To, co model přečetl, je jen návrh. Doklad vznikne, až ho tady potvrdíte.",
    queueEmpty:
      "Fronta je prázdná. Doklady sem přicházejí z aplikace <scanner>Netlion Scanner</scanner> v telefonu nebo přeposláním do schránky výše.",
    inQueue: "Ve frontě: {{total}}",
    dropbox: {
      uploading: "Nahrávám…",
      upload: "Nahrát doklad",
      hint: "Nebo přetáhněte PDF či fotku kamkoli do tohoto rámečku. Doklad se zařadí do stejné fronty jako ty z telefonu a ze schránky — přečte ho model, potvrdíte ho vy.",
      refused: "Soubor {{name}} byl odmítnut",
      failed: "Nahrání se nezdařilo.",
    },
    mailbox: {
      title: "Přeposlat fakturu e-mailem",
      description:
        "Pošlete ji na <address>{{address}}</address> a Daniela přílohu načte do této fronty. Nic nezaúčtuje — potvrzujete to stále vy.",
      watching: "sleduje se",
      notSetUp: "nenastaveno",
      queueing: "Zařazuji…",
      checkNow: "Zkontrolovat teď",
      queueFailed: "Načtení se nepodařilo zařadit.",
      noPassword:
        "Schránka zatím nemá heslo (<code>DOCUMENTS_IMAP_PASS</code>), takže se nic nečte.",
      lastRead: "Poslední načtení",
      isRunning: "právě probíhá",
      lastReadCounts: ": zpráv {{messages}}, převzatých dokladů {{accepted}}.",
      notTaken: "Z posledního načtení nepřevzato:",
      noSender: "(bez odesílatele)",
      restricted:
        "E-maily se přebírají od lidí s účtem v Hubu a z adres v kontaktech účetnictví; vše ostatní se odmítne a vypíše se tady.",
    },
    fakturoid: {
      title: "Krabice na doklady ve Fakturoidu",
      description:
        "Soubory vložené do „Krabice na doklady“ se přenášejí každých 30 minut a ve Fakturoidu zůstávají.",
      queueing: "Zařazuji…",
      pullNow: "Stáhnout teď",
      queueFailed: "Stažení se nepodařilo zařadit.",
      noPull: "Zatím neproběhlo žádné stažení.",
      lastPull: "Poslední stažení",
      isRunning: "právě probíhá",
      counts: ": souborů v krabici {{files}}, nových tady {{added}}.",
    },
    scan: {
      untitled: "Naskenovaný doklad",
      byEmail: "e-mailem od {{sender}}",
      senderUnverified: "Neověřený odesílatel",
      senderUnverifiedHint:
        "Poštovní server nepotvrdil, že e-mail opravdu přišel z této adresy ({{reason}}). Před zaplacením doklad ověřte u dodavatele.",
      withSubject: ' — „{{subject}}"',
      fromFakturoid: "z krabice na doklady ve Fakturoidu",
      withNote: " ({{note}})",
      unknown: "neznámý",
      reading: "čte se…",
      unreadable: "nečitelný",
      readAgain: "Přečíst znovu",
      document: "Doklad",
      openPdf: "Otevřít PDF",
      unreadableFields: "Na dokladu je, ale nečitelné:",
      documentType: "Typ dokladu",
      kind: {
        invoice: "faktura",
        receipt: "účtenka",
        credit_note: "dobropis",
        other: "jiný",
      },
      correctsNo: "Opravuje fakturu č.",
      creditNoteHint:
        "Částky jsou to, co se vrací; Hub je drží záporné. Dobropis přesně na daň z faktury je vrácení daně a v přiznání k DPH se neobjeví; jakýkoli jiný se řídí fakturou.",
      registrationNo: "IČO",
      vatNo: "DIČ",
      documentNumber: "Číslo dokladu",
      variableSymbol: "Variabilní symbol",
      issued: "Vystaveno",
      taxableSupply: "DUZP",
      due: "Splatnost",
      received: "Přijato",
      currency: "Měna",
      rateToCzk: "Kurz k CZK",
      rateLoading: "Hledám kurz ČNB…",
      rateMissing: "ČNB pro tento den kurz nevyhlásila — doplňte kurz z bankovního výpisu.",
      rateFound: "Kurz ČNB k datu zdanitelného plnění: {{rate}} CZK.",
      seedDescription: "Plnění dle dokladu",
      base: "Základ",
      vat: "DPH",
      total: "Celkem",
      paidInCash: "Zaplaceno na místě v hotovosti",
      paidFromBank: "Zaplaceno z banky",
      prepaid:
        "Zaplaceno z předplaceného kreditu: karta kredit jen dobila, takže tento doklad žádný pohyb neuhrazuje. Založte ho jako nezaplacený.",
      noMovement:
        "Zatím žádný bankovní pohyb nesedí (částka, datum kolem faktury). Založte doklad jako nezaplacený; spáruje se, jakmile platba dorazí.",
      card: "karta",
      noneOfThese: "žádný z nich, založit jako nezaplacený",
      replaces:
        "Znovu načteno z originálu dokladu <originalDocument>{{number}}</originalDocument> ({{partner}}, {{total}} {{currency}}, jak je měl Fakturoid). Vytvořením tohoto dokladu se ten původní stornuje a převezme jeho úhrady.",
      mayBeHere: "Tohle tu už možná je:",
      sameNumber: "stejné číslo",
      sameAmountAndDay: "stejná částka a den",
      createAnyway: "Je to jiný doklad, přesto ho vytvořit",
      creating: "Vytvářím…",
      create: "Vytvořit doklad",
      discardReason: "Důvod zamítnutí",
      discard: "Zamítnout",
      duplicateConflict:
        "Vypadá to na doklad, který tu už je. Zkontrolujte ho, nebo potvrďte níže.",
      createFailed: "Doklad se nepodařilo vytvořit.",
      discardFailed: "Zamítnutí se nezdařilo.",
      rereadFailed: "Čtení se nepodařilo spustit.",
    },
    paymentReason: {
      variable_symbol: "variabilní symbol",
      amount: "částka",
      card_amount: "částka na kartě",
      supplier_name: "dodavatel",
      date: "stejný den",
      together: "dohromady dají částku",
    },
    fields: {
      supplier_name: "dodavatel",
      supplier_registration_no: "IČO",
      supplier_vat_no: "DIČ",
      document_number: "číslo dokladu",
      variable_symbol: "variabilní symbol",
      issued_on: "datum vystavení",
      taxable_on: "datum zdanitelného plnění",
      due_on: "datum splatnosti",
      currency: "měna",
      subtotal: "základ",
      vat_total: "DPH",
      vat_rate: "sazba DPH",
      total: "celkem",
      payment_method: "způsob úhrady",
      document_kind: "typ dokladu",
    },
    missingFile: {
      title: "Archivovaný soubor chybí.",
      email:
        "E-mail je pořád ve schránce, takže ho lze stáhnout zpět — řádek si ve frontě drží své místo i to, co už model přečetl.",
      fetching: "Stahuji…",
      fetch: "Znovu stáhnout ze schránky",
      fakturoid:
        "Přišel z krabice na doklady ve Fakturoidu. Stáhněte ho odtamtud, tento řádek zamítněte a soubor nahrajte znovu.",
      upload:
        "Přišel z nahrání, takže není odkud ho stáhnout zpět — řádek zamítněte a doklad pošlete znovu.",
      fetchFailed: "Znovu stažení se nezdařilo.",
    },
    supplyType: {
      service: "služba",
      goods: "zboží",
      goods_with_installation: "zboží s montáží",
      construction: "stavební práce",
      import: "dovezené zboží",
      other: "něco jiného",
    },
    vat: {
      thisIs: "Jde o",
      domesticReverseCharge: "Na dokladu je „daň odvede zákazník“ (§92a)",
      supplyCode: "Kód předmětu plnění (B.1)",
      supplyCodeChoose: "Vyberte…",
      notDeductible: "Neuplatňovat odpočet DPH (doklad není vystaven na firmu): zůstane v nákladu",
      weAssess: "Daň přiznáváme my",
      supplierAccounts: "Daň přiznává dodavatel",
      needsLook: " · chce to zkontrolovat",
      returnLabel: "Přiznání",
      returnLines: "řádky {{lines}}",
      returnDeductionOnly: "— jen odpočet",
      returnDeduction: ", odpočet {{lines}}",
      controlStatement: " · kontrolní hlášení {{section}}",
      overriddenSelf: "Ručně jste nastavili, že daň přiznáváme my.",
      overriddenSupplier: "Ručně jste nastavili, že daň přiznává dodavatel.",
      useRules: "Použít znovu pravidla",
      disagree: "Nesouhlasíte?",
      switchToSupplier: "Daň přiznává dodavatel",
      switchToUs: "Daň přiznáváme my",
    },
    lines: {
      breakIntoLines: "Rozdělit na řádky",
      description: "Popis",
      qty: "Množ.",
      unit: "Jednotka",
      unitPrice: "Cena za jednotku",
      vatPct: "DPH %",
      removeLine: "odebrat řádek",
      addLine: "Přidat řádek",
      base: "Základ",
      vatWeAssess: "DPH, které přiznáváme",
      vat: "DPH",
      payable: "K úhradě dodavateli",
      total: "Celkem",
    },
    supplier: {
      label: "Dodavatel",
      notInBook: "— není v adresáři —",
      pick: "Vyberte z adresáře…",
      nameOnDocument: "Jméno na dokladu",
      notInBookHint: "Není v adresáři: přidá se při vytvoření dokladu.",
      adding: "Přidávám…",
      addNow: "Přidat do adresáře hned",
      addFailed: "Dodavatele se nepodařilo přidat.",
    },
    rereadAll: {
      queueFailed: "Čtení se nepodařilo zařadit.",
      confirm:
        "Přečíst znovu všech {{n}} čekajících dokladů? Úpravy v jejich formulářích se ztratí.",
      queueing: "Zařazuji…",
      reading: "Čtu… zbývá {{left}}",
      button: "Přečíst vše znovu",
      reloads: "Seznam se obnoví, až se přečte poslední.",
      hint: "Přečte čekající doklady znovu (země dodavatele, plátce DPH, odběratel).",
    },
    duplicates: {
      summary: "Odloženo jako duplicity ({{n}})",
      document: "doklad",
      bringBack: "vrátit zpět",
      restoreFailed: "Vrácení se nezdařilo.",
    },
  },
};
